import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";

import Carousel from "../components/Carousel";
import EllipsisLoader from "../components/EllipsisLoader";
import FloorplanCustomiser from "../components/FloorplanCustomiser";
import FloorplanLightbox from "../components/FloorplanLightbox";
import FullscreenModal from "../components/FullscreenModal";
import HouseFilters from "../components/HouseFilters";
import HouseTypeSelector from "../components/HouseTypeSelector";
import Icon from "../components/Icon";
import SearchResultsBar from "../components/SearchResultsBar";
import Selectable from "../components/Selectable";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import useWindowSize from "../hooks/useWindowSize";
import { getPageTitle } from "../resources/routes";
import { FloorplanOption,Home,HouseType,IconName,LandType } from "../types";
import { scrollToTop,scrollToWithOffset } from "../util";

const ChooseFloorplanScreen = observer(() => {
  const apiContext = useContext(ApiContext);
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const location = useLocation();

  const windowSize = useWindowSize();

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [lightboxSvg, setLightboxSvg] = useState("");
  const [isLightboxShown, setIsLightboxShown] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [carouselWidth, setCarouselWidth] = useState(100);
  const [hasSortChanged, setHasSortChanged] = useState(false);

  const debouncedGetHomesFromApi = useDebouncedCallback(() => {
    apiContext.resetHomeSearchPagination();
    apiContext.getHomes(1, hasSortChanged);
  }, 1000);

  useEffect(() => {
    scrollToTop();
    navContext.setFooterNavVisible(true);
    navContext.setNextButtonVisible(true);
    navContext.setLoginButtonVisible(false);
    navContext.setBackButtonVisible(false);
    navContext.setRestartButtonVisible(false);
    navContext.setNextButtonVisible(true);
    navContext.setNextBtnText("Next");
    navContext.resetNextBtn();
    
    navContext.setBackButtonVisible(false);
    navContext.setRestartButtonVisible(false);

    userContext.setSelectedFacade(null);
    userContext.resetSelectedFacadeFinishOption();

    navContext.setOnNavigateBack(() => {
      // Reset the form values when the user goes back
      apiContext.setHouseType(null);
      apiContext.resetHouseFilters();
      userContext.setSelectedHome(null);
      userContext.resetSelectedFloorplanOptions();
      userContext.resetIsFloorplanFlipped();
      navContext.setSubtotalVisible(false);
    });

    handleFooterNavVisibilty();

    return () => {
      // Unbind the handler when we unmount
      navContext.resetOnNavigateBack();
    };
  }, []);

  useEffect(() => {
    // When search filters change, debounce search for new homes
    if (apiContext.houseType !== null) {
      apiContext.setIsLoading(true);
      userContext.setSelectedHome(null);
      setCurrentSlideIndex(0);
      debouncedGetHomesFromApi();
    }
  }, [
    apiContext.houseType,
    apiContext.numBeds,
    apiContext.numBaths,
    apiContext.numCars,
    apiContext.numLiving,
    apiContext.searchText,
    apiContext.lotWidth,
    apiContext.sortMethod,
  ]);

  useEffect(() => {
    handleFooterNavVisibilty();
  }, [
    apiContext.houseType,
    userContext.selectedHome,
    userContext.subtotalPrice,
  ]);

  useEffect(() => {
    if (apiContext.enteredViaHome && !userContext.selectedHome) {
      scrollToSearch();
      const existingHomeIndex = apiContext.homeSearchResultsWithPlaceholders
        .map((item) => (item ? item.id : -1))
        .indexOf(apiContext.enteredViaHome.id);
      if (existingHomeIndex > -1) {
        setCurrentSlideIndex(existingHomeIndex);

        userContext.setSelectedHome(
          apiContext.homeSearchResultsWithPlaceholders[existingHomeIndex]
        );

        // Now remove the entered via home link so they can choose other homes etc
        apiContext.setEnteredViaHome(null);
      }
    }
  }, [
    apiContext.homeSearchResultsWithPlaceholders,
    apiContext.enteredViaHome,
    userContext.selectedHome,
  ]);

  // If on mobile, we need to auto-select the currently viewed home when it loads
  useEffect(() => {
    if (windowSize.width <= 992) {
      if (
        apiContext.homeSearchResultsWithPlaceholders.length > currentSlideIndex
      ) {
        const targetHome =
          apiContext.homeSearchResultsWithPlaceholders[currentSlideIndex];
        if (targetHome !== null) {
          onClickHome(targetHome, currentSlideIndex);
        }
      }
    }
  }, [
    apiContext.homeSearchResultsWithPlaceholders,
    windowSize.width,
    currentSlideIndex,
  ]);

  useEffect(() => {
    if (windowSize.width <= 992 && apiContext.homeSearchResultsWithPlaceholders.length && apiContext.homeSearchResultsWithPlaceholders[0] !== null) {
      setTimeout(function () {
        // https://zimpledigital.teamwork.com/#/tasks/223303 - Scroll to results section when selecting a House Type
        scrollToWithOffset(".floorplans-container .pyht-carousel-inner", -50);
      }, 250);
    }
  }, [
    apiContext.homeSearchResultsWithPlaceholders,
    windowSize.width,
  ]);

  useEffect(() => {
    if (userContext.isKioskMode || windowSize.width <= 1080) {
      setCarouselWidth(100);
    } else {
      setCarouselWidth(77);
    }
  }, [userContext.isKioskMode, windowSize.width]);

  const handleFooterNavVisibilty = () => {
    navContext.setNextButtonEnabled(
      apiContext.houseType !== null && userContext.selectedHome !== null
    );
    navContext.setSubtotalVisible(
      apiContext.houseType !== null &&
        userContext.selectedHome !== null &&
        userContext.subtotalPrice > 0
    );
  };

  const onSlideHome = (homeIndex: number) => {
    if (windowSize.width <= 992) {
      // Mobile only
      if (apiContext.homeSearchResultsWithPlaceholders.length > homeIndex) {
        userContext.setSelectedHome(
          apiContext.homeSearchResultsWithPlaceholders[homeIndex]
        );
      } else {
        userContext.setSelectedHome(null);
      }
    }
    setCurrentSlideIndex(homeIndex);

    getHomesPageIfNeeded(homeIndex);
  };

  const onClickHome = (home: Home, index: number) => {
    userContext.setSelectedHome(home);
    setCurrentSlideIndex(index);
  };

  const onClickHouseType = (option: HouseType) => {
    apiContext.toggleHouseType(option);
    handleFooterNavVisibilty();
  };

  const scrollToSearch = () => {
    if (windowSize.width <= 992) {
      setTimeout(function () {
        // https://zimpledigital.teamwork.com/#/tasks/223303 - Scroll to results section when selecting a House Type
        scrollToWithOffset(".floorplans-container", -50);
      }, 250);
    }
  };

  const onClickFloorplanOption = (homeId: number, option: FloorplanOption) => {
    userContext.toggleSelectedFloorplanOption(homeId, option);
    handleFooterNavVisibilty();
  };

  const onFlipFloorplan = (homeId: number, isFlipped: boolean) => {
    userContext.setIsFloorplanFlipped(homeId, isFlipped);
  };

  const onClickShowLightbox = (svg: string) => {
    // The user can click the lightbox link on the next item in the carousel on desktop
    setLightboxSvg(svg);
    setIsLightboxShown(true);
  };

  const onClickMobilePrev = () => {
    if (currentSlideIndex > 0) {
      const index = currentSlideIndex - 1;
      getHomesPageIfNeeded(index);
      setCurrentSlideIndex(index);

      const targetHome = apiContext.homeSearchResultsWithPlaceholders[index];
      userContext.setSelectedHome(targetHome);
    }
  };

  const onClickMobileNext = () => {
    if (currentSlideIndex < apiContext.homeSearchTotalResults - 1) {
      const index = currentSlideIndex + 1;
      getHomesPageIfNeeded(index);
      setCurrentSlideIndex(index);
      const targetHome = apiContext.homeSearchResultsWithPlaceholders[index];
      userContext.setSelectedHome(targetHome);
    }
  };

  const getHomesPageIfNeeded = (index: number) => {
    // Find the page that the item is on and if we need to load new results for that page
    const pageNum = Math.ceil((index + 1) / apiContext.homeSearchPageSize);
    if (apiContext.homeSearchResultsPaged[pageNum] === undefined) {
      apiContext.getHomes(pageNum, hasSortChanged);
    }
  };

  const onCloseSearchModal = () => {
    setIsModalOpen(false);
    setIsModalClosing(false);
  };

  const onChangeSort = () => {
    console.log("Choose: onChangeSort");
    setHasSortChanged(true);
  };

  return (
    <div className={"screen screen-choose-floorplan"}>
      <div className={"container"}>
        <h1>{getPageTitle(location.pathname)}</h1>
        <p className={"subtitle"}>
          Let&apos;s find a home that suits your land.
        </p>

        <p className={"section-label"}>Select House Type</p>
        <HouseTypeSelector
          onClick={onClickHouseType}
          selected={apiContext.houseType}
        />
      </div>
      {apiContext.houseType !== null ? (
        <div className={"container floorplans-container"}>
          {windowSize.width > 992 ? (
            <div className={"filters-desktop"}>
              <p className={"section-label"}>Filter By</p>
              <HouseFilters
                beds={apiContext.numBeds}
                living={apiContext.numLiving}
                baths={apiContext.numBaths}
                cars={apiContext.numCars}
                searchText={apiContext.searchText}
                onClickBeds={(val) => apiContext.toggleNumBeds(val)}
                onClickLiving={(val) => apiContext.toggleNumLiving(val)}
                onClickBaths={(val) => apiContext.toggleNumBaths(val)}
                onClickCars={(val) => apiContext.toggleNumCars(val)}
                onSearchChange={(val) => apiContext.setSearchText(val)}
                onScreenKeyboard={userContext.isKioskMode}
                inlineLabels
              />
            </div>
          ) : (
            <div
              className={"btn btn-secondary filters-mobile"}
              onClick={() => setIsModalOpen(true)}
            >
              Refine Your Search
              <Icon name={IconName.CHEVRON_DOWN} />
            </div>
          )}
          <SearchResultsBar 
            landType={LandType.VACANT_BLOCK}
            onChangeSort={() => onChangeSort()}
          />
          {apiContext.isLoading && !apiContext.isLoadingNextPage ? (
            <div />
          ) : apiContext.homeSearchResultsWithPlaceholders.length > 0 ? (
            <div className={"floorplan-results"}>
              <Carousel
                onSlide={onSlideHome}
                currentSlideIndex={currentSlideIndex}
                numItems={apiContext.homeSearchTotalResults}
                itemWidthPerc={carouselWidth}
                spacingPx={windowSize.width > 992 ? 30 : 0}
                items={apiContext.homeSearchResultsWithPlaceholders.map(
                  (home, index) => {
                    if (home == null) {
                      return (
                        <div key={index}>
                          <Selectable
                            selected={false}
                            hidden={windowSize.width <= 992}
                          >
                            <div className={"floorplan-loading"}>
                              <EllipsisLoader />
                            </div>
                          </Selectable>
                        </div>
                      );
                    }

                    return (
                      <div key={index} onClick={() => onClickHome(home, index)}>
                        <Selectable
                          selected={
                            userContext.selectedHome !== null &&
                            userContext.selectedHome.id === home.id
                          }
                          hidden={windowSize.width <= 992}
                          className={`floorplan-${home.house_type}`}
                        >
                          <FloorplanCustomiser
                            home={home}
                            isActive={
                              userContext.selectedHome !== null &&
                              userContext.selectedHome.id === home.id
                            }
                            isFloorplanFlipped={
                              userContext.isFloorplanFlipped.get(home.id) ||
                              false
                            }
                            onFlipFloorplan={(isFlipped) =>
                              onFlipFloorplan(home.id, isFlipped)
                            }
                            selectedOptions={
                              userContext.selectedFloorplanOptions.get(
                                home.id
                              ) || []
                            }
                            onClickShowLightbox={onClickShowLightbox}
                            onClickOption={(floorplanOption) =>
                              onClickFloorplanOption(
                                home.id,
                                floorplanOption as FloorplanOption
                              )
                            }
                            onClickMobilePrev={onClickMobilePrev}
                            onClickMobileNext={onClickMobileNext}
                            floorplanNumber={index + 1}
                            totalFloorplans={apiContext.homeSearchTotalResults}
                          />
                        </Selectable>
                      </div>
                    );
                  }
                )}
              />
            </div>
          ) : windowSize.width < 992 ? (
            <p className={"section-label no-results"}>
              No Homes Match Your Criteria
            </p>
          ) : null}
        </div>
      ): (
        <div className={"container section promo"}>
          {apiContext.chooseFloorplanSettings.promo_desktop_image && (
            <img
              className={"desktop"}
              src={apiContext.chooseFloorplanSettings.promo_desktop_image}
              alt={""}
            />
          )}
          {apiContext.chooseFloorplanSettings.promo_mobile_image && (
            <img
              className={"mobile"}
              src={apiContext.chooseFloorplanSettings.promo_mobile_image}
              alt={""}
            />
          )}
        </div>
      )}
      {windowSize.width < 992 && isModalOpen && (
        <FullscreenModal
          title={"Refine Your Search"}
          onClose={onCloseSearchModal}
          closing={isModalClosing}
        >
          <div className={"container"}>
            <HouseFilters
              beds={apiContext.numBeds}
              living={apiContext.numLiving}
              baths={apiContext.numBaths}
              cars={apiContext.numCars}
              searchText={apiContext.searchText}
              onClickBeds={(val) => apiContext.toggleNumBeds(val)}
              onClickLiving={(val) => apiContext.toggleNumLiving(val)}
              onClickBaths={(val) => apiContext.toggleNumBaths(val)}
              onClickCars={(val) => apiContext.toggleNumCars(val)}
              onSearchChange={(val) => apiContext.setSearchText(val)}
              onScreenKeyboard={userContext.isKioskMode}
              inlineLabels
            />
          </div>
        </FullscreenModal>
      )}
      {isLightboxShown && userContext.selectedHome && (
        <FloorplanLightbox
          home={userContext.selectedHome}
          svg={lightboxSvg}
          selectedOptions={
            userContext.selectedFloorplanOptions.get(
              userContext.selectedHome.id
            ) || []
          }
          onClose={() => setIsLightboxShown(false)}
        />
      )}
      {userContext.isKioskMode && userContext.showKioskSplash && (
        <div className="splash" onClick={() => userContext.setShowKioskSplash(false)}>
          <img src={apiContext.kioskSplash} alt="Splash" />
        </div>
      )}
    </div>
  );
});

export default ChooseFloorplanScreen;
